import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export const EDIT_PRODUCT_TABS = Object.freeze({
  info: { defaultSection: null },
  categories: { defaultSection: null },
  "filters-images": { defaultSection: "filters" },
  "note-product": { defaultSection: null },
  // Product Bundles, deliberately listed before Compare (?tab=bundles).
  bundles: { defaultSection: null },
  compare: { defaultSection: null },
  // Existing conditional CJ tab. It remains URL-addressable so the refactor
  // does not remove an established product-management feature.
  sync: { defaultSection: null },
});

export const EDIT_PRODUCT_FILTER_SECTIONS = Object.freeze(["filters", "display", "images"]);
export const EDIT_PRODUCT_IMAGE_VIEWS = Object.freeze(["add-images", "link-filters", "publication-gallery"]);
export const DEFAULT_EDIT_PRODUCT_IMAGE_VIEW = "add-images";

const LEGACY_KEYS = ["info", "cat", "adFil", "edFil", "preFil", "addImg"];

const isLegacyEnabled = (params, key) => {
  const value = params.get(key);
  return value === "true" || value === "1";
};

const resolveLegacyLocation = (params) => {
  if (isLegacyEnabled(params, "cat")) return { tab: "categories", section: null, view: null };
  if (isLegacyEnabled(params, "adFil") || isLegacyEnabled(params, "edFil")) {
    return { tab: "filters-images", section: "filters", view: null };
  }
  if (isLegacyEnabled(params, "preFil")) return { tab: "filters-images", section: "display", view: null };
  if (isLegacyEnabled(params, "addImg")) {
    return {
      tab: "filters-images",
      section: "images",
      view: isLegacyEnabled(params, "LkIngFil") ? "link-filters" : DEFAULT_EDIT_PRODUCT_IMAGE_VIEW,
    };
  }
  if (isLegacyEnabled(params, "info")) return { tab: "info", section: null, view: null };
  return null;
};

export const resolveEditProductLocation = (params) => {
  const legacy = params.get("tab") ? null : resolveLegacyLocation(params);
  const rawRequestedTab = legacy?.tab || params.get("tab");
  // Compatibility for links created before the public slug became
  // `note-product`.
  const requestedTab = rawRequestedTab === "notes" ? "note-product" : rawRequestedTab;
  const tab = Object.prototype.hasOwnProperty.call(EDIT_PRODUCT_TABS, requestedTab)
    ? requestedTab
    : "info";
  const requestedSection = legacy?.section ?? params.get("section");
  const section = tab === "filters-images"
    ? (EDIT_PRODUCT_FILTER_SECTIONS.includes(requestedSection) ? requestedSection : "filters")
    : null;
  const requestedView = legacy?.view ?? params.get("view");
  const view = tab === "filters-images" && section === "images"
    ? (EDIT_PRODUCT_IMAGE_VIEWS.includes(requestedView) ? requestedView : DEFAULT_EDIT_PRODUCT_IMAGE_VIEW)
    : null;
  return { tab, section, view };
};

export const updateEditProductParams = (currentParams, updates) => {
  const next = new URLSearchParams(currentParams);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") next.delete(key);
    else next.set(key, value);
  });
  return next;
};

export const normalizeEditProductParams = (currentParams, location) => {
  const next = updateEditProductParams(currentParams, location);
  if (!location.section) next.delete("section");
  if (!location.view) next.delete("view");
  LEGACY_KEYS.forEach((key) => next.delete(key));
  next.delete("LkIngFil");
  return next;
};

export const useEditProductNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = resolveEditProductLocation(searchParams);
  const normalizedSearch = normalizeEditProductParams(searchParams, location).toString();

  useEffect(() => {
    if (normalizedSearch !== searchParams.toString()) {
      setSearchParams(new URLSearchParams(normalizedSearch), { replace: true });
    }
  }, [normalizedSearch, searchParams, setSearchParams]);

  const changeTab = useCallback((requestedTab) => {
    const tab = Object.prototype.hasOwnProperty.call(EDIT_PRODUCT_TABS, requestedTab) ? requestedTab : "info";
    setSearchParams(normalizeEditProductParams(searchParams, {
      tab,
      section: EDIT_PRODUCT_TABS[tab].defaultSection,
      view: null,
    }));
  }, [searchParams, setSearchParams]);

  const changeSection = useCallback((requestedSection) => {
    const section = EDIT_PRODUCT_FILTER_SECTIONS.includes(requestedSection) ? requestedSection : "filters";
    setSearchParams(normalizeEditProductParams(searchParams, {
      tab: "filters-images",
      section,
      view: section === "images" ? DEFAULT_EDIT_PRODUCT_IMAGE_VIEW : null,
    }));
  }, [searchParams, setSearchParams]);

  const changeView = useCallback((requestedView) => {
    const view = EDIT_PRODUCT_IMAGE_VIEWS.includes(requestedView)
      ? requestedView
      : DEFAULT_EDIT_PRODUCT_IMAGE_VIEW;
    setSearchParams(normalizeEditProductParams(searchParams, {
      tab: "filters-images",
      section: "images",
      view,
    }));
  }, [searchParams, setSearchParams]);

  return {
    ...location,
    changeTab,
    changeSection,
    changeView,
  };
};
